/** 站点树展示用元数据（与 list_sites / 小程序 entry_site_options 对齐） */
export type SiteTreeMeta = {
  id: number;
  site_name: string;
  /** 一级站点为 null/0；二级为所属一级 id */
  parent_site_id?: number | null;
};

/** 列表 / 勾选区：一级与其二级子站分组展示 */
export type SiteHierarchyDisplayRow<T extends SiteTreeMeta = SiteTreeMeta> = {
  site: T;
  depth: 0 | 1;
  subline?: string;
};

export function buildSiteHierarchyDisplayRows<T extends SiteTreeMeta>(sites: T[]): SiteHierarchyDisplayRow<T>[] {
  const byId = new Map(sites.map((x) => [x.id, x]));
  const ordered: SiteHierarchyDisplayRow<T>[] = [];
  const seen = new Set<number>();
  const roots = sites.filter((x) => !x.parent_site_id).sort((a, b) => a.id - b.id);
  const childrenOf = (pid: number) =>
    sites.filter((x) => x.parent_site_id === pid).sort((a, b) => a.id - b.id);

  for (const r of roots) {
    ordered.push({ site: r, depth: 0 });
    seen.add(r.id);
    for (const c of childrenOf(r.id)) {
      ordered.push({
        site: c,
        depth: 1,
        subline: `隶属一级站点「${r.site_name}」`,
      });
      seen.add(c.id);
    }
  }

  for (const x of [...sites].sort((a, b) => a.id - b.id)) {
    if (seen.has(x.id)) continue;
    const pid = x.parent_site_id;
    const parent = pid ? byId.get(pid) : undefined;
    const subline = pid
      ? parent
        ? `隶属一级站点「${parent.site_name}」`
        : `父站点 ID ${pid}`
      : undefined;
    ordered.push({ site: x, depth: pid ? 1 : 0, subline });
    seen.add(x.id);
  }

  return ordered;
}

export function siteHierarchyHasChildren(sites: SiteTreeMeta[]): boolean {
  return sites.some((s) => (s.parent_site_id ?? 0) > 0);
}

/** 一级一块 + 其下二级多条（用于勾选区、站点切换 chips 等紧凑布局） */
export type SiteHierarchyGroup<T extends SiteTreeMeta = SiteTreeMeta> = {
  parent: SiteHierarchyDisplayRow<T> | null;
  children: SiteHierarchyDisplayRow<T>[];
};

export function buildSiteHierarchyGroups<T extends SiteTreeMeta>(sites: T[]): SiteHierarchyGroup<T>[] {
  const rows = buildSiteHierarchyDisplayRows(sites);
  const groups: SiteHierarchyGroup<T>[] = [];
  let parent: SiteHierarchyDisplayRow<T> | null = null;
  let children: SiteHierarchyDisplayRow<T>[] = [];

  const flush = (): void => {
    if (parent !== null || children.length > 0) {
      groups.push({ parent, children: [...children] });
    }
    parent = null;
    children = [];
  };

  for (const row of rows) {
    if (row.depth === 0) {
      flush();
      parent = row;
    } else if (parent !== null) {
      children.push(row);
    } else {
      groups.push({ parent: null, children: [row] });
    }
  }
  flush();

  return groups;
}

/** 下拉 / 勾选列表：含层级与展示文案 */
export type CatalogSiteOptionVM = {
  id: number;
  site_name: string;
  level: number;
  /** 供原生 <option> 或纯文本树形展示 */
  treeLabel: string;
};

export function catalogSiteDepthInScope(siteId: number, siteById: Map<number, SiteTreeMeta>, optionIds: Set<number>): number {
  let depth = 0;
  let cur = siteById.get(siteId);
  let guard = 0;
  while (cur && guard++ < 16) {
    const pid = cur.parent_site_id != null && cur.parent_site_id > 0 ? cur.parent_site_id : 0;
    if (pid < 1 || !optionIds.has(pid)) {
      break;
    }
    depth += 1;
    cur = siteById.get(pid);
  }
  return depth;
}

export function formatCatalogSiteTreeLabel(siteName: string, level: number, anyChildInList: boolean): string {
  const name = siteName.trim() || `站点`;
  if (level <= 0) {
    return anyChildInList ? `${name}（一级）` : name;
  }
  const indent = '\u3000'.repeat(level);
  const branch = '\u2514 ';
  const tierLabel = level === 1 ? '二级' : `子级·${level}`;
  return `${indent}${branch}${name}\u2003（${tierLabel}）`;
}

export function buildCatalogSiteOptionVMs(
  base: Array<{ id: number; site_name: string }>,
  allSites: SiteTreeMeta[],
): CatalogSiteOptionVM[] {
  if (base.length === 0) {
    return [];
  }
  const siteById = new Map(allSites.map((s) => [s.id, s]));
  const optionIds = new Set(base.map((b) => b.id));

  const withLevel = base.map((b) => {
    const meta = siteById.get(b.id);
    const nameFromMeta = meta?.site_name?.trim();
    const siteName = (nameFromMeta && nameFromMeta.length > 0 ? nameFromMeta : b.site_name) || `站点 #${b.id}`;
    const level = allSites.length > 0 ? catalogSiteDepthInScope(b.id, siteById, optionIds) : 0;
    return { id: b.id, site_name: siteName, level };
  });

  const anyChild = withLevel.some((r) => r.level > 0);

  const rows = withLevel.map((r) => ({
    ...r,
    treeLabel: formatCatalogSiteTreeLabel(r.site_name, r.level, anyChild),
  }));

  const roots = rows.filter((r) => r.level === 0).sort((a, b) => a.id - b.id);
  const out: CatalogSiteOptionVM[] = [];
  const walk = (n: CatalogSiteOptionVM) => {
    out.push(n);
    const kids = rows
      .filter((x) => {
        const pid = siteById.get(x.id)?.parent_site_id;
        return (pid ?? 0) === n.id;
      })
      .sort((a, b) => a.id - b.id);
    for (const k of kids) {
      walk(k);
    }
  };
  for (const r of roots) {
    walk(r);
  }
  const seen = new Set(out.map((x) => x.id));
  for (const r of [...rows].sort((a, b) => a.id - b.id)) {
    if (!seen.has(r.id)) {
      out.push(r);
    }
  }
  return out;
}
